// Text-to-Image model catalog for fal.ai endpoints

export type ImageModelProvider =
  | 'google'
  | 'openai'
  | 'fal'
  | 'recraft'
  | 'krea'
  | 'bytedance'
  | 'alibaba'
  | 'lumina'
  | 'omnigen'
  | 'aura'
  | 'open-source'
  | 'stability';

export interface ImageModelPricing {
  startingAt: string;
  details?: string;
}

export interface ImageModelInfo {
  id: string;
  name: string;
  endpoint: string;
  provider: ImageModelProvider;
  tier: 'Top Tier' | 'Fast Generation' | 'Open Source & Specialized';
  speedCategory: 'Ultra Fast' | 'Very Fast' | 'Fast' | 'Standard';
  description: string;
  bestFor: string;
  highlights: string[];
  pricing: ImageModelPricing;
  extraNotes?: string;
}

export interface ImageModelCategory {
  name: string;
  description: string;
  models: ImageModelInfo[];
}

export const IMAGE_MODEL_CATEGORIES: ImageModelCategory[] = [
  {
    name: 'Top Tier Models (Highest Quality)',
    description: 'Premium endpoints tuned for photorealism, typography, and long-form prompt fidelity.',
    models: [
      {
        id: 'openai/gpt-image-2',
        name: 'GPT Image 2',
        endpoint: 'openai/gpt-image-2',
        provider: 'openai',
        tier: 'Top Tier',
        speedCategory: 'Standard',
        description: "OpenAI's latest ChatGPT image model on fal.ai with strong typography and product photography.",
        bestFor: 'Hero assets, product photos, UI mockups, text-heavy marketing visuals',
        highlights: [
          'ChatGPT Images 2.0 model',
          'Strong text rendering',
          'Quality tiers for cost control'
        ],
        pricing: {
          startingAt: '$0.01 per image',
          details: 'Low quality starts around $0.01/image; high quality 4K can reach about $0.41/image'
        },
      },
      {
        id: 'fal-ai/imagen4/preview',
        name: 'Imagen 4 Preview',
        endpoint: 'fal-ai/imagen4/preview',
        provider: 'google',
        tier: 'Top Tier',
        speedCategory: 'Standard',
        description: "Google's highest quality photorealistic model with enterprise guardrails.",
        bestFor: 'Hero banners, polished marketing visuals, enterprise brand work',
        highlights: [
          'Photorealistic lighting and detail',
          'Enterprise-grade reliability',
          'Partner preview access'
        ],
        pricing: {
          startingAt: '$0.05 per image',
          details: 'Balanced quality tier surfaced in the fal.ai console'
        },
        extraNotes: 'Fast and hyper tiers are also available inside fal.ai for different quality/speed trade-offs.'
      },
      {
        id: 'fal-ai/flux-pro/v1.1',
        name: 'FLUX.1 Pro v1.1',
        endpoint: 'fal-ai/flux-pro/v1.1',
        provider: 'fal',
        tier: 'Top Tier',
        speedCategory: 'Very Fast',
        description: 'Enhanced FLUX Pro with superior composition, 6x faster inference, and top Elo ranking.',
        bestFor: 'Production-ready photorealistic campaigns and ads',
        highlights: [
          'High Elo score on Artificial Analysis',
          'Raw mode for post-processing',
          'Handles complex compositions'
        ],
        pricing: {
          startingAt: '$0.055 per megapixel',
          details: 'Usage-based billing straight from fal.ai'
        },
      },
      {
        id: 'fal-ai/flux-pro/v1.1-ultra',
        name: 'FLUX.1 Pro v1.1 Ultra',
        endpoint: 'fal-ai/flux-pro/v1.1-ultra',
        provider: 'fal',
        tier: 'Top Tier',
        speedCategory: 'Very Fast',
        description: 'Professional-grade FLUX with up to 2K resolution support and raw output.',
        bestFor: '2K renders, high-touch art direction, controllable lighting',
        highlights: [
          'Up to 2048px outputs',
          'Raw output for custom pipelines',
          'Extended LoRA compatibility'
        ],
        pricing: {
          startingAt: 'Usage-based',
          details: 'Premium tier shown after enabling the Ultra plan in fal.ai'
        },
      },
      {
        id: 'fal-ai/flux-pro/kontext/text-to-image',
        name: 'FLUX Pro Kontext',
        endpoint: 'fal-ai/flux-pro/kontext/text-to-image',
        provider: 'fal',
        tier: 'Top Tier',
        speedCategory: 'Fast',
        description: 'Kontext-optimized FLUX variant with flawless typography and unmatched prompt following.',
        bestFor: 'Text-heavy assets, branding boards, UI mocks',
        highlights: [
          'Best-in-class typography',
          'Highly literal prompt interpretation',
          'Photorealistic rendering'
        ],
        pricing: {
          startingAt: 'Usage-based',
          details: 'Same credit-based pricing as other FLUX Pro tiers via fal.ai'
        },
      },
      {
        id: 'fal-ai/recraft/v3',
        name: 'Recraft V3',
        endpoint: 'fal-ai/recraft/v3',
        provider: 'recraft',
        tier: 'Top Tier',
        speedCategory: 'Fast',
        description: 'SOTA model on Hugging Face benchmarks. Excels at vector art, text, and branded imagery.',
        bestFor: 'Logo explorations, vector scenes, long text layouts',
        highlights: [
          'Vector-native output fidelity',
          'Brand-consistent stylization',
          'Long text rendering'
        ],
        pricing: {
          startingAt: 'Usage-based',
          details: 'Available through the fal.ai model marketplace'
        },
      },
    ],
  },
  {
    name: 'Fast Generation Models',
    description: 'Endpoints tuned for speed-sensitive workflows with strong quality.',
    models: [
      {
        id: 'fal-ai/recraft/v3/text-to-image',
        name: 'Recraft V3 Text-to-Image',
        endpoint: 'fal-ai/recraft/v3/text-to-image',
        provider: 'recraft',
        tier: 'Fast Generation',
        speedCategory: 'Fast',
        description: 'Dedicated text-to-image endpoint with 60+ style presets and vector, illustration, or realistic modes.',
        bestFor: 'Creative direction boards, marketing concept tests',
        highlights: [
          'Preset-driven workflows',
          'Excellent typography',
          'Vector + raster output'
        ],
        pricing: {
          startingAt: '$0.04 per image',
          details: 'Vector styles billed at roughly $0.08 per image'
        },
      },
      {
        id: 'fal-ai/flux/dev',
        name: 'FLUX.1 [dev]',
        endpoint: 'fal-ai/flux/dev',
        provider: 'fal',
        tier: 'Fast Generation',
        speedCategory: 'Fast',
        description: '12B parameter flow transformer with excellent prompt adherence and image-to-image support.',
        bestFor: 'High volume experimentation, workflows needing I2I',
        highlights: [
          'Image-to-image + text-to-image',
          'Great prompt adherence',
          '12B parameter flow transformer'
        ],
        pricing: {
          startingAt: '$0.025 per megapixel',
          details: 'Usage-based pricing exposed in fal.ai'
        },
      },
      {
        id: 'fal-ai/flux/schnell',
        name: 'FLUX.1 [schnell]',
        endpoint: 'fal-ai/flux/schnell',
        provider: 'fal',
        tier: 'Fast Generation',
        speedCategory: 'Ultra Fast',
        description: 'Sub-second generation in 1-4 steps while maintaining strong quality.',
        bestFor: 'Realtime previews, iterative design loops, live tools',
        highlights: [
          '1-4 inference steps',
          'Streaming-quality previews',
          'Surprisingly sharp output for the speed'
        ],
        pricing: {
          startingAt: '$0.003 per megapixel',
          details: 'Optimized for rapid prototyping budgets'
        },
      },
      {
        id: 'fal-ai/flux-krea-lora/stream',
        name: 'FLUX Krea LoRA Stream',
        endpoint: 'fal-ai/flux-krea-lora/stream',
        provider: 'krea',
        tier: 'Fast Generation',
        speedCategory: 'Very Fast',
        description: 'Streaming FLUX [dev] variant with LoRA support for custom styles and live previews.',
        bestFor: 'Interactive styling tools, LoRA-enabled design systems',
        highlights: [
          'LoRA conditioning',
          'Realtime streaming output',
          'Compatible with Krea style library'
        ],
        pricing: {
          startingAt: 'Usage-based streaming',
          details: 'Charged by request duration/GPU seconds inside fal.ai'
        },
      },
      {
        id: 'fal-ai/nano-banana',
        name: 'Nano Banana',
        endpoint: 'fal-ai/nano-banana',
        provider: 'google',
        tier: 'Fast Generation',
        speedCategory: 'Fast',
        description: "Google's state-of-the-art image generation and editing model with guardrails.",
        bestFor: 'Editorial imagery, light editing, marketing visuals',
        highlights: [
          'Supports image editing',
          'Google quality & safety',
          'Fast iteration times'
        ],
        pricing: {
          startingAt: '$0.039 per image',
          details: 'Transparent pricing displayed before each request'
        },
      },
    ],
  },
  {
    name: 'Open Source & Specialized Models',
    description: 'Blends of open-source, multimodal, and niche endpoints for customization.',
    models: [
      {
        id: 'fal-ai/hidream-i1/full',
        name: 'HiDream I1 Full',
        endpoint: 'fal-ai/hidream-i1/full',
        provider: 'fal',
        tier: 'Open Source & Specialized',
        speedCategory: 'Standard',
        description: '17B open-source model reaching SOTA quality within seconds.',
        bestFor: 'Teams needing open weights with Fal hosting',
        highlights: [
          'Open-source checkpoints',
          'Great balance of quality + speed',
          'Handles reasoning-heavy prompts'
        ],
        pricing: {
          startingAt: 'Usage-based',
          details: 'Billed the same way as other hosted open models in fal.ai'
        },
      },
      {
        id: 'fal-ai/seedream-4.0',
        name: 'Seedream 4.0',
        endpoint: 'fal-ai/seedream-4.0',
        provider: 'bytedance',
        tier: 'Open Source & Specialized',
        speedCategory: 'Standard',
        description: 'ByteDance unified generation + editing model trained entirely on licensed data.',
        bestFor: 'Enterprises wanting licensed-only datasets',
        highlights: [
          'Unified gen/edit architecture',
          'Licensed training data',
          'Balanced realism and style'
        ],
        pricing: {
          startingAt: 'Usage-based',
          details: 'Pricing surfaced in fal.ai dashboard'
        },
      },
      {
        id: 'fal-ai/qwen-image',
        name: 'Qwen Image',
        endpoint: 'fal-ai/qwen-image',
        provider: 'alibaba',
        tier: 'Open Source & Specialized',
        speedCategory: 'Fast',
        description: 'Foundation model with advanced text rendering and precise editing.',
        bestFor: 'Typography-heavy creative, controllable edits',
        highlights: [
          'Advanced text fidelity',
          'Robust editing controls',
          'Strong multilingual support'
        ],
        pricing: {
          startingAt: '$0.02 per megapixel',
          details: 'Discounted price available for high-volume tiers'
        },
      },
      {
        id: 'fal-ai/lumina-image/v2',
        name: 'Lumina Image v2',
        endpoint: 'fal-ai/lumina-image/v2',
        provider: 'lumina',
        tier: 'Open Source & Specialized',
        speedCategory: 'Fast',
        description: '2B parameter flow-based diffusion with enhanced typography under Apache 2.0.',
        bestFor: 'Developers needing OSS licensing with premium hosting',
        highlights: [
          'Apache 2.0 license',
          'Great typography and captions',
          'Flow matching quality'
        ],
        pricing: {
          startingAt: '$0.075 per megapixel',
          details: 'Transparent usage-based plan via fal.ai'
        },
      },
      {
        id: 'fal-ai/omnigen-v1',
        name: 'OmniGen v1',
        endpoint: 'fal-ai/omnigen-v1',
        provider: 'omnigen',
        tier: 'Open Source & Specialized',
        speedCategory: 'Fast',
        description: 'Unified model supporting multi-modal prompts, personalization, and try-on.',
        bestFor: 'Virtual try-on, multi-input creative briefs',
        highlights: [
          'Personalization slots',
          'Multi-modal conditioning',
          'Virtual try-on ready'
        ],
        pricing: {
          startingAt: 'Usage-based',
          details: 'Billed per request (see fal.ai console)'
        },
      },
      {
        id: 'fal-ai/aura-flow',
        name: 'AuraFlow v0.3',
        endpoint: 'fal-ai/aura-flow',
        provider: 'aura',
        tier: 'Open Source & Specialized',
        speedCategory: 'Fast',
        description: '6.8B open-source flow-based model scoring 0.703 on GenEval.',
        bestFor: 'Open-source fine-tuning or cost-sensitive deployments',
        highlights: [
          'GenEval 0.703 performance',
          'Flow matching architecture',
          'Great starting point for LoRA'
        ],
        pricing: {
          startingAt: 'Open-source friendly',
          details: 'Free to self-host, pay-as-you-go on fal.ai'
        },
      },
      {
        id: 'fal-ai/imagen3',
        name: 'Imagen 3',
        endpoint: 'fal-ai/imagen3',
        provider: 'google',
        tier: 'Open Source & Specialized',
        speedCategory: 'Standard',
        description: 'Google partner model delivering high-quality photorealistic images.',
        bestFor: 'Agencies with partner access needing Google quality',
        highlights: [
          'Partner-only access',
          'Photorealistic output',
          'Strong safety filters'
        ],
        pricing: {
          startingAt: 'Partner pricing',
          details: 'Requires enterprise agreement with Google/fal'
        },
      },
      {
        id: 'fal-ai/kolors',
        name: 'Kolors',
        endpoint: 'fal-ai/kolors',
        provider: 'fal',
        tier: 'Open Source & Specialized',
        speedCategory: 'Standard',
        description: 'High-quality multilingual model that excels at Asian art styles and 8K quality.',
        bestFor: 'Stylized campaigns, multilingual typography',
        highlights: [
          '8K-ready outputs',
          'Great at Asian-inspired styles',
          'Multilingual prompt support'
        ],
        pricing: {
          startingAt: 'Usage-based',
          details: 'Billed per request in fal.ai'
        },
      },
      {
        id: 'fal-ai/stable-diffusion-v35-large',
        name: 'Stable Diffusion 3.5 Large',
        endpoint: 'fal-ai/stable-diffusion-v35-large',
        provider: 'stability',
        tier: 'Open Source & Specialized',
        speedCategory: 'Standard',
        description: 'Latest SD 3.5 large checkpoint with enhanced reasoning and open licensing.',
        bestFor: 'Custom fine-tunes, OSS compliant workflows',
        highlights: [
          'Open-source license',
          'Improved detail vs SDXL',
          'Great for LoRA training'
        ],
        pricing: {
          startingAt: 'Usage-based',
          details: 'Pay only for what you generate on fal.ai or self-host for free'
        },
      },
      {
        id: 'fal-ai/stable-diffusion-v35-large-turbo',
        name: 'Stable Diffusion 3.5 Large Turbo',
        endpoint: 'fal-ai/stable-diffusion-v35-large-turbo',
        provider: 'stability',
        tier: 'Open Source & Specialized',
        speedCategory: 'Very Fast',
        description: 'Turbo SD 3.5 variant optimized for 4-8 inference steps while keeping quality.',
        bestFor: 'Fast SD 3.5 generations, bulk experimentation',
        highlights: [
          '4-8 step guidance',
          'Maintains SD 3.5 detail',
          'Supports open fine-tuning'
        ],
        pricing: {
          startingAt: 'Usage-based',
          details: 'Same per-request pricing as SD 3.5 large but optimized for fewer steps'
        },
      },
    ],
  },
];

export const ALL_IMAGE_MODELS = IMAGE_MODEL_CATEGORIES.flatMap((category) => category.models);

// Default image model ID (used when no model is explicitly selected)
export const DEFAULT_IMAGE_MODEL = 'openai/gpt-image-2';

export const getImageModelById = (id: string): ImageModelInfo | undefined => {
  return ALL_IMAGE_MODELS.find((model) => model.id === id);
};
